import { Lightning, Registry } from '@lightningjs/sdk';
import AbstractPlayerEventHandler from './AbstractPlayerEventHandler';
import { LiveEventStream } from 'types/api/media';
import {
  getVideoReportingPayload,
  reportPlayback,
} from 'services/analytics/reportingServiceVideo';
import { PlaybackEvent } from 'types/analytics';
import { setJitterTimeout } from 'support/playerUtils';

export default class LivePlayerEventHandler
  extends AbstractPlayerEventHandler
  implements
    Lightning.Component.ImplementTemplateSpec<AbstractPlayerEventHandler>
{
  private _liveStream: LiveEventStream | null = null;
  private _liveStreamStart: number | null = null;
  private _liveStreamPlaybackStart: number | null = null;
  private _liveEventEndTimeoutId: number | null = null;
  private _pendingReportLiveEventEnd: (() => void) | undefined = undefined;

  override initializePlayerContent(mediaContent: LiveEventStream) {
    this._liveStream = mediaContent;

    this._liveStreamStart = new Date(
      this._liveStream.startDateStream,
    ).getTime();
    this._liveStreamPlaybackStart = Date.now();
  }

  override resetAnalytics() {
    super.resetAnalytics();
    this.clearLiveEventEndTimeout();
  }

  private handleLiveEventEndUpdate(rawTime: number) {
    if (
      !this._liveStream ||
      !this._liveStreamStart ||
      !this._liveStreamPlaybackStart
    )
      return;

    if (!this._canCreditEvent) return;

    let calculatedTime = (Date.now() - this._liveStreamStart) / 1000;
    const hist: number | undefined = this._liveStream.has_dvr
      ? this._liveStream.hist * 60
      : undefined;
    if (hist && calculatedTime > hist) {
      /*
      the rawTime increases with time, even when paused
      to adjust for this we subtract the time since initialization, 
      this will still be 5-10 seconds off but close enough for our calculations   
      */
      const adjustedRawTime =
        rawTime - (Date.now() - this._liveStreamPlaybackStart) / 1000;
      const dvrProgress = hist - adjustedRawTime; // progress is measured backwards from hist
      calculatedTime = calculatedTime - dvrProgress;
    }

    if (calculatedTime < this._liveStream.durationSecs) return;

    const payloadRef = getVideoReportingPayload();

    this._pendingReportLiveEventEnd = () => {
      reportPlayback(
        PlaybackEvent.LIVE_EVENT_END,
        { relativeTime: calculatedTime, rawTime: calculatedTime },
        payloadRef,
      );
    };

    this._liveEventEndTimeoutId = setJitterTimeout(() => {
      this._pendingReportLiveEventEnd?.();

      this._liveEventEndTimeoutId = null;
      this._pendingReportLiveEventEnd = undefined;
    });
    this._canCreditEvent = false;
    this._hasReachedCreditsOnce = true;
  }

  private clearLiveEventEndTimeout() {
    // If the staggered reporting is set, fire it off immediately
    if (this._liveEventEndTimeoutId !== null) {
      Registry.clearTimeout(this._liveEventEndTimeoutId);
      this._pendingReportLiveEventEnd?.();

      this._liveEventEndTimeoutId = null;
      this._pendingReportLiveEventEnd = undefined;
    }
  }

  /* =============================
   * Events
   * ============================= */

  override $videoPlayerTimeUpdate(rawTime: number) {
    if (!this._Player || !this._liveStream) return;

    this.handleLiveEventEndUpdate(rawTime);
    super.$videoPlayerTimeUpdate(rawTime);
  }
}
